import Vue from 'vue'
import database from '@/utils/database'
import Socket from './Socket'
import { getSources } from './Source'

const LOCK_FLAG = 1

function stringToSlug (str) {
  str = str.replace(/^\s+|\s+$/g, '') // trim
  str = str.toLowerCase()

  // remove accents, swap ñ for n, etc
  var from = 'àáäâèéëêìíïîòóöôùúüûñç·/_,:;'
  var to = 'aaaaeeeeiiiioooouuuunc------'
  for (var i = 0, l = from.length; i < l; i++) {
    str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i))
  }

  str = str.replace(/[^a-z0-9 -]/g, '') // remove invalid chars
    .replace(/\s+/g, '-') // collapse whitespace and replace by -
    .replace(/-+/g, '-') // collapse dashes

  return str
}

export default class Item {
  constructor (data, characterHash) {
    this.data = data
    this._characterHash = characterHash
  }

  get characterHash () {
    if (this.definition.equippable) {
      return this._characterHash
    }
    if (this.currentBucket.hash === 138197802) {
      return 'VAULT'
    } else {
      return 'INVENTORY'
    }
  }

  set characterHash (value) {
    this._characterHash = value
  }

  get equipped () {
    return this.instance.isEquipped
  }

  get key () {
    return this.data.itemInstanceId
  }

  get sources () {
    return getSources(this.data.itemHash) || []
  }

  get icon () {
    if (this.definition.displayProperties.icon) {
      return 'https://www.bungie.net' + this.definition.displayProperties.icon
    }
    return ''
  }

  get name () {
    return this.definition.displayProperties.name
  }

  get categories () {
    if (this.definition.itemCategoryHashes) {
      return this.definition.itemCategoryHashes.map(cat => database.getCategory(cat))
    }
  }

  get instance () {
    if (this.data.itemInstanceId) {
      const instance = Vue.store.state.destiny.profile.itemComponents.instances[this.data.itemInstanceId]
      if (!instance) {
        console.warning('no instance for ' + this.name + ' ' + this.data.itemInstanceId)
        return {}
      }
      return instance
    }
    return {}
  }

  get damageType () {
    if (this.instance.damageTypeHash) {
      return database.getDamageType(this.instance.damageTypeHash)
    }
    return {}
  }

  get damageTypeIcon () {
    if (this.damageType.displayProperties) {
      return '//www.bungie.net' + this.damageType.displayProperties.icon
    }
  }

  get damageTypeName () {
    if (this.damageType.displayProperties) {
      return this.damageType.displayProperties.name
    }
    return ''
  }

  get primaryStat () {
    if (this.instance.primaryStat) {
      return this.instance.primaryStat.value
    }
    return 0
  }

  get realPrimaryStat () {
    if (this.primaryStat) {
      return this.primaryStat - (this.hasLegenderyMod ? 5 : 0)
    }
    return 0
  }

  get character () {
    return Vue.store.getters['destiny/profile/getCharacter'](this.characterHash)
  }

  get definition () {
    return database.getItemDefinition(this.data.itemHash)
  }

  get enabled () {
    if (this.definition.redacted) {
      return false
    }
    if (this.definition.nonTransferrable) {
      return false
    }
    if (this.isLost) { // lost items
      return false
    }
    return true
  }

  get isLost () {
    return this.data.bucketHash === 215593132
  }

  get canToggleLock () {
    return this.data.lockable
  }

  get canEquip () {
    return this.instance.canEquip
  }

  get sockets () {
    if (this.data.itemInstanceId) {
      const sockets = Vue.store.state.destiny.profile.itemComponents.sockets[this.data.itemInstanceId]
      if (sockets) {
        return sockets.sockets
          .filter(socket => socket.plugHash)
          .map(socket => new Socket(database.getItemDefinition(socket.plugHash)))
      }
    }
    return []
  }

  get displayableSockets () {
    return this.sockets.filter(itemDefinition => itemDefinition.itemCategoryHashes.length && itemDefinition.itemCategoryHashes.indexOf(52) !== -1)
  }

  get isTransferable () {
    return this.canTransferTo(Vue.store.state.destiny.profile.selectedCharacter)
  }

  canTransferTo (selectedCharacter) {
    if (this.characterHash === 'INVENTORY') {
      return selectedCharacter === 'VAULT'
    }

    return this.characterHash !== selectedCharacter
  }

  get tierType () {
    return database.getTierType(this.definition.inventory.tierTypeHash) || {}
  }

  get bucket () {
    return database.getInventoryBucket(this.definition.inventory.bucketTypeHash)
  }

  get currentBucket () {
    return database.getInventoryBucket(this.data.bucketHash)
  }

  get hasLegenderyMod () {
    return this.sockets.find(socket => socket.inventory.tierTypeName === 'Legendary' && socket.itemCategoryHashes.indexOf(52) !== -1) !== undefined
  }

  get isWeapon () {
    return this.bucket.id === 1498876634 || this.bucket.id === 2465295065 || this.bucket.id === 2465295065
  }

  get isArmor () {
    return this.bucket.id === 3448274439 || this.bucket.id === 3551918588 || this.bucket.id === 14239492 || this.bucket.id === 20886954 || this.bucket.id === 1585787867
  }

  get isImportant () {
    return this.tierType.id === 2759499571 || this.tierType.id === 4008398120
  }

  get tokens () {
    return [...new Set([
      'on:' + this.character.className,
      'tier:' + (this.tierType.displayProperties || {}).name || '',
      'type:' + this.bucket.displayProperties.name,
      'sub-type:' + this.definition.itemTypeDisplayName,
      this.isWeapon || this.isArmor ? ('importance:' + (this.isImportant ? 'important' : 'not-important')) : '',
      this.isDuplicate ? 'duplicated' : 'different',
      this.isDuplicate ? this.isBestDuplicate ? 'best-duplicate' : 'worst-duplicate' : '',
      this.favorited ? 'favorited' : 'unfavorited',
      this.equipped ? 'equipped' : 'unequipped',
      this.isLocked ? 'state:locked' : 'state:unlocked',
      this.isWeapon ? 'meta:weapon' : 'meta:not-weapon',
      this.isArmor ? 'meta:armor' : 'meta:not-armor',
      this.isTransferable ? 'transferable' : 'untransferable',
      ...this.categories.map(c => 'category:' + c.shortTitle),
      ...this.sockets.map(socket => 'mod:' + (socket.itemCategoryHashes.indexOf(52) !== -1 ? socket.inventory.tierTypeName + ':' : '') + socket.displayProperties.name),
      ...this.sources.map(set => 'source:' + stringToSlug(set)),
      ...this.tags.map(tag => 'tag:' + stringToSlug(tag))
    ].filter(token => token).map(token => token.toLowerCase().replace(/ /g, '-')))]
  }

  /* check if search token is found in item */
  checkToken (token) {
    if ((this.name.toLowerCase() + ' ' + this.tokens.join(' ')).indexOf(token) !== -1) {
      return true
    }
    // eslint-disable-next-line no-useless-escape
    const re = /^(\d+)([\-\+](\d*))?$/.exec(token)
    const count = this.primaryStat || this.data.quantity
    if (re) {
      const from = parseInt(re[1])
      if (re[2] === undefined) {
        if (from === count) {
          return true
        }
      } else if (count >= from) {
        const to = parseInt(re[3])
        if (isNaN(to)) {
          return true
        } else {
          return count <= to
        }
      }
    }
    return false
  }

  get primaryStatDefinition () {
    return this.definition.stats.primaryBaseStatHash
  }

  get stats () {
    return Object.values(this.definition.stats.stats)
      .map(
        stat => {
          return {
            value: stat.value,
            hash: stat.statHash,
            definition: database.getStat(stat.statHash)
          }
        }
      )
      .filter(stat => stat.value && stat.definition.displayProperties.name && stat.hash !== this.instance.primaryStat.statHash)
      .sort((a, b) => a.definition.index < b.definition.index ? -1 : 1)
  }

  get favorited () {
    return Vue.store.getters.isFavorited && Vue.store.getters.isFavorited({item: this})
  }

  get isDuplicate () {
    if (this._isDuplicated === undefined) {
      if (this.data.itemInstanceId) {
        this._isDuplicated = Vue.store.state.destiny.profile.items.find(item => item.data.itemHash === this.data.itemHash && item.data.itemInstanceId !== this.data.itemInstanceId) !== undefined
      } else {
        this._isDuplicated = false
      }
    }
    return this._isDuplicated
  }

  get isBestDuplicate () {
    if (this.isDuplicate) {
      if (this._isBestDuplicate === undefined) {
        if (this.data.itemInstanceId) {
          this._isBestDuplicate = Vue.store.state.destiny.profile.items
            .find(item => item.data.itemHash === this.data.itemHash &&
              item.data.itemInstanceId !== this.data.itemInstanceId &&
              this.primaryStat >= item.primaryStat) || false
        } else {
          this._isBestDuplicate = true
        }
      }
      return this._isBestDuplicate
    }
    return undefined
  }

  /* actions */
  transfer (character) {
    return new Promise((resolve, reject) => {
      if (this.canTransferTo(character.characterId)) {
        return Vue.store.dispatch('destiny/profile/transferItem', {item: this, character}).then(() => resolve())
      } else {
        throw Error('Cannot transfer not transferable item')
      }
    })
  }

  get siblings () {
    return Vue.store.state.destiny.profile.items.filter(search => search.bucket === this.bucket && search.character === this.character)
  }

  equip () {
    // this.equipped = true
    if (this.canEquip) {
      return Vue.store.dispatch('destiny/profile/equipItem', {item: this})
    } else {
      throw Error('Cannot equip not equipable item')
    }
  }

  unEquip () {
    return Vue.store.dispatch('destiny/profile/unEquipItem', {item: this})
  }

  get isLocked () {
    return (this.data.state & LOCK_FLAG) === 1
  }

  lock () {
    if (this.canToggleLock) {
      return Vue.store.dispatch('destiny/profile/lockItem', {item: this})
    }
  }

  unLock () {
    return Vue.store.dispatch('destiny/profile/unLockItem', {item: this})
  }

  get tags () {
    return Object.keys(Vue.store.state.tagsDefinitions)
      .filter(tag => this.hasTag(tag))
  }

  hasTag (tag) {
    return new Set(Vue.store.state.tags[tag]).has(this.key)
  }

  toggleTag (tag) {
    return Vue.store.commit('toggleTag', {item: this, tag})
  }
}



// WEBPACK FOOTER //
// ./src/domain/Item.js